import { BeatSequencer } from '@/story/sequencer';
import { BLACKOUT_HALL_BEAT, BLACKOUT_HALL_CUES, BLACKOUT_HALL_FLAGS, BLACKOUT_HALL_IDS } from '@/story/act2/blackoutHall';
import { BLACKOUT_FUSE_INTERACT_AT, BLACKOUT_OPEN_CRATES, BLACKOUT_PASS_PEN_Z, blackoutCrateInteractAt } from '@/world/fattening';
import { BlackoutHallDirector } from '@/world/blackoutHallDirector';
import type { Interactable } from '@/systems/interaction';
import type { BeatPartCapabilities, BeatPartModule, BeatPartModuleDefinition } from '@/game/beatPartModule';

const CRATE_IDS = [BLACKOUT_HALL_IDS.crateA, BLACKOUT_HALL_IDS.crateB] as const;

/**
 * Beat 8 part 2: the fuse, the dark, the crates, the flock, the torch.
 *
 * Until session 83 this was a branch in `main.ts` plus lines in five other
 * places there. Where each piece now runs, against where it ran: the three
 * world-fact flags and the two nudge handlers are in `fixedStep`, which runs
 * after interaction events rather than on either side of them — both are still
 * before `story.update` in the same tick, so the story sees the same flags on
 * the same tick. The cue handling, the director's render update and the
 * fade/prompt terms are where the host's generic part calls sit.
 */
function createBlackoutHallPartModule(capabilities: BeatPartCapabilities): BeatPartModule {
  const world = capabilities.world;
  if (world === undefined) throw new Error('beat 8 / part 2: the host offered no world capability');
  const seam = world.room.blackoutHall;
  if (seam === undefined) throw new Error('beat 8 / part 2: the room offers no blackout hall seam — booted outside the returned hall');
  const director = new BlackoutHallDirector(world.scene, seam.fuseBox, seam.lights);
  const fade = capabilities.createFade({ open: BLACKOUT_HALL_CUES.open, fadeOut: BLACKOUT_HALL_CUES.fadeOut });
  const story = new BeatSequencer(BLACKOUT_HALL_BEAT);
  const interactables: readonly Interactable[] = [
    {
      id: BLACKOUT_HALL_IDS.fuse,
      at: BLACKOUT_FUSE_INTERACT_AT,
      verbs: ['nudge'],
      reach: 1.15,
      enabled: false,
    },
    // The two crates whose lids are clear. Which two is `fattening.ts`'s call
    // and not this file's — see `BLACKOUT_OPEN_CRATES`, and D-056 for what a
    // second list of the same decision costs.
    ...BLACKOUT_OPEN_CRATES.map((_crate, index) => ({
      id: index === 0 ? BLACKOUT_HALL_IDS.crateA : BLACKOUT_HALL_IDS.crateB,
      at: blackoutCrateInteractAt(index),
      verbs: ['nudge'] as const,
      reach: 0.95,
      enabled: false,
    })),
  ];
  let disposed = false;

  return {
    story,
    interactables,
    get fade(): number { return fade.fade; },
    get promptSuppressed(): boolean { return !disposed && fade.fade > 0.5; },
    fixedStep({ player, raiseFlag, nudged }): void {
      if (player.z >= BLACKOUT_PASS_PEN_Z) raiseFlag(BLACKOUT_HALL_FLAGS.passedPen);
      // World facts the director settles on the render step; the story can
      // only see them as flags, raised here where the sequencer reads them.
      if (director.flockSettled) raiseFlag(BLACKOUT_HALL_FLAGS.flockSettled);
      if (director.torchGone) raiseFlag(BLACKOUT_HALL_FLAGS.torchGone);

      CRATE_IDS.forEach((id, index) => {
        if (nudged?.(id) !== true) return;
        director.openCrate(index);
        world.interaction.setEnabled(id, false);
        // Read by no stage: `wayGoals.ts` points her at the other crate once
        // this one is open (D-209).
        raiseFlag(id);
        world.playOneShot('hens-panic', { gain: 0.7, position: blackoutCrateInteractAt(index) });
      });
      if (nudged?.(BLACKOUT_HALL_IDS.fuse) === true) {
        raiseFlag(BLACKOUT_HALL_FLAGS.fuseThrown);
        world.interaction.setEnabled(BLACKOUT_HALL_IDS.fuse, false);
        world.playOneShot('fuse-throw', { gain: 0.72, position: BLACKOUT_FUSE_INTERACT_AT });
      }
    },
    handleCue(cue): void {
      fade.handle(cue);
      director.handle(cue);
      if (cue.id === BLACKOUT_HALL_CUES.offerFuse) world.interaction.setEnabled(BLACKOUT_HALL_IDS.fuse, true);
      // The fuse sound contains the last fraction of a second of hum and then a
      // deliberate silence. Cutting the beds on the same cue that starts its
      // animation makes the room stop on the lever rather than after the
      // engine's normal walk-out fade, while leaving Rosalie and later hens audible.
      if (cue.id === BLACKOUT_HALL_CUES.throwFuse) world.cutAmbience();
      if (cue.id === BLACKOUT_HALL_CUES.offerCrates) {
        for (const id of CRATE_IDS) world.interaction.setEnabled(id, true);
      }
    },
    renderStep(frameDt, her): void {
      if (disposed) return;
      // Clamped as the director clamps its own delta, which is what the fade
      // was fed while it lived there: one long frame (a tab coming back) must
      // not jump the overlay.
      fade.update(Number.isFinite(frameDt) ? Math.max(0, Math.min(0.1, frameDt)) : 0);
      director.update(frameDt, her);
    },
    diagnostics() {
      return {
        kind: 'blackout-hall',
        blackout: {
          freed: director.freed,
          flockSettled: director.flockSettled,
          torchGone: director.torchGone,
          noticed: director.noticed,
        },
      };
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      director.dispose();
      fade.dispose();
    },
  };
}

export const BLACKOUT_HALL_PART_MODULE: BeatPartModuleDefinition = {
  beat: 8,
  part: 2,
  room: 'fattening',
  create: createBlackoutHallPartModule,
};
